/**
 * L1J Web Client - EventEmitter
 * 클라이언트 내부 이벤트 시스템
 */

/**
 * 이벤트 콜백 타입
 */
type EventCallback<T = unknown> = (data: T) => void;

/**
 * 이벤트 리스너 정보
 */
interface ListenerInfo {
  callback: EventCallback;
  once: boolean;
}

/**
 * EventEmitter 클래스
 *publish-subscribe 패턴을 사용하여 클라이언트 내부 이벤트 시스템을 구현
 */
export class EventEmitter {
  private events: Map<string, ListenerInfo[]> = new Map();
  private globalEvents: ListenerInfo[] = [];

  /**
   * 이벤트 리스너 등록
   * @param event 이벤트 이름
   * @param callback 이벤트 발생 시 호출될 콜백 함수
   */
  on<T = unknown>(event: string, callback: EventCallback<T>): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push({ callback, once: false });
  }

  /**
   *一次性 이벤트 리스너 등록
   * @param event 이벤트 이름
   * @param callback 이벤트 발생 시 호출될 콜백 함수
   */
  once<T = unknown>(event: string, callback: EventCallback<T>): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push({ callback, once: true });
  }

  /**
   * 글로벌 이벤트 리스너 등록 (모든 이벤트 수신)
   * @param callback 모든 이벤트 발생 시 호출될 콜백 함수
   */
  onAny<T = unknown>(callback: (event: string, data: T) => void): void {
    this.globalEvents.push({
      callback: callback as EventCallback,
      once: false
    });
  }

  /**
   * 글로벌 이벤트 리스너 해제 (onAny로 등록한 리스너 제거)
   * @param callback 제거할 콜백 함수
   */
  offAny<T = unknown>(callback: (event: string, data: T) => void): void {
    const index = this.globalEvents.findIndex(info => info.callback === (callback as EventCallback));
    if (index !== -1) {
      this.globalEvents.splice(index, 1);
    }
  }

  /**
   * 이벤트 리스너 해제
   * @param event 이벤트 이름
   * @param callback 제거할 콜백 함수
   */
  off<T = unknown>(event: string, callback: EventCallback<T>): void {
    const listeners = this.events.get(event);
    if (!listeners) return;

    const index = listeners.findIndex(info => info.callback === callback);
    if (index !== -1) {
      listeners.splice(index, 1);
    }

    if (listeners.length === 0) {
      this.events.delete(event);
    }
  }

  /**
   * 리스너 해제
   * @param event 이벤트 이름 (없으면 모든 이벤트)
   */
  offAll(event?: string): void {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
      this.globalEvents = [];
    }
  }

  /**
   * 이벤트 발생
   * @param event 이벤트 이름
   * @param data 이벤트 데이터
   */
  emit<T = unknown>(event: string, data?: T): void {
    // 글로벌 리스너 먼저 호출 — 스냅샷 순회로 콜백 안에서의 onAny/offAny에 영향받지 않게 함
    for (const info of [...this.globalEvents]) {
      try {
        (info.callback as (event: string, data: T) => void)(event, data);
      } catch (error) {
        console.error(`[EventEmitter] Global listener error in "${event}":`, error);
      }
    }

    const listeners = this.events.get(event);
    if (!listeners) return;

    // 스냅샷 순회 — 콜백 안에서 off()/on()을 호출해도 리스너가
    // 스킵되거나(splice로 인덱스 밀림) 즉시 호출되는 일이 없도록 한다.
    for (const info of [...listeners]) {
      // once 리스너는 재진입 emit에서 중복 호출되지 않도록 호출 전에 제거
      if (info.once) {
        const current = this.events.get(event);
        if (current) {
          const index = current.indexOf(info);
          if (index === -1) continue; // 이미 다른 곳(재진입/off)에서 제거됨
          current.splice(index, 1);
        }
      }

      try {
        info.callback(data);
      } catch (error) {
        console.error(`[EventEmitter] Listener error in "${event}":`, error);
      }
    }

    // 빈 배열 정리
    const remaining = this.events.get(event);
    if (remaining && remaining.length === 0) {
      this.events.delete(event);
    }
  }

  /**
   * 특정 이벤트 리스너 수 반환
   * @param event 이벤트 이름
   */
  listenerCount(event: string): number {
    return this.events.get(event)?.length ?? 0;
  }

  /**
   * 등록된 모든 이벤트 이름 반환
   */
  eventNames(): string[] {
    return Array.from(this.events.keys());
  }

  /**
   * 현재 등록된 리스너 수 반환
   */
  get size(): number {
    let count = 0;
    for (const listeners of this.events.values()) {
      count += listeners.length;
    }
    return count;
  }
}

/**
 * 전역 이벤트 버스 인스턴스
 * 게임 전반의 이벤트 통신에 사용
 */
export const gameEvents = new EventEmitter();

/**
 * 네트워크 이벤트 버스 인스턴스
 * Gateway 패킷 처리에 사용
 */
export const networkEvents = new EventEmitter();

/**
 * UI 이벤트 버스 인스턴스
 * UI 상태 변경에 사용
 */
export const uiEvents = new EventEmitter();

/**
 * 디폴트 내보내기 (gameEvents와 동일)
 */
export default gameEvents;
